// src/pages/Reports/ClientRetentionLiteReportPage.js
//
// Client Retention Lite Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents a lightweight client-retention view based only on the
// visits table.
//
// MAIN BUSINESS QUESTIONS
// -----------------------
// - How many distinct clients appeared in the selected period?
// - How many were one-time vs repeat clients?
// - How much revenue came from repeat clients?
// - Which clients are the strongest repeat clients?
//
// WHY THIS PAGE IS IMPORTANT
// --------------------------
// Many operational reports describe:
// - when demand happens,
// - what category is strong,
// - where losses happen.
//
// This page adds another management dimension:
// - how sticky the client base appears during the selected period.
//
// IMPORTANT INTERPRETATION
// ------------------------
// This is a Lite retention report, not a perfect CRM truth.
//
// It uses the client name already stored in visits.
// That makes it useful immediately, but still pragmatic in interpretation.
//
// CURRENT BACKEND SHAPE
// --------------------
// {
//   period,
//   filters,
//   methodology,
//   summary,
//   client_rows,
//   visit_count_distribution,
//   top_repeat_clients
// }
//
// UI GOALS
// --------
// 1) Keep the page visually aligned with the rest of the reporting pages.
// 2) Show top-level retention indicators first.
// 3) Surface the repeat vs one-time split clearly.
// 4) Keep detailed client rows available for review.
//

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchClientRetentionLiteReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import "./ClientRetentionLiteReportPage.css";

/* -------------------------------------------------------------------------- */
/* Small formatting helpers                                                   */
/* -------------------------------------------------------------------------- */

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

function formatPercentFromRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.0%";
  return `${(n * 100).toFixed(1)}%`;
}

function retentionBadgeClassName(value) {
  const raw = String(value || "").toLowerCase();

  if (raw === "one_time") return "retention-badge retention-badge--one-time";
  if (raw === "repeat_2") return "retention-badge retention-badge--repeat";
  if (raw === "repeat_3") return "retention-badge retention-badge--repeat";
  if (raw === "repeat_4_5") return "retention-badge retention-badge--strong";
  if (raw === "repeat_6_plus") return "retention-badge retention-badge--strong";
  return "retention-badge retention-badge--muted";
}

function retentionBucketLabel(value, t) {
  /**
   * Keep retention bucket code mapping in the frontend, but keep the visible
   * label in i18n. The backend should continue returning stable technical
   * bucket codes such as one_time / repeat_2 / repeat_6_plus.
   */
  switch (String(value || "")) {
    case "one_time":
      return t("reports:clientRetentionLite.buckets.oneTime");
    case "repeat_2":
      return t("reports:clientRetentionLite.buckets.repeat2");
    case "repeat_3":
      return t("reports:clientRetentionLite.buckets.repeat3");
    case "repeat_4_5":
      return t("reports:clientRetentionLite.buckets.repeat45");
    case "repeat_6_plus":
      return t("reports:clientRetentionLite.buckets.repeat6Plus");
    default:
      return t("reports:common.unknown", { defaultValue: "Unknown" });
  }
}

function resolveMethodologyNotes(methodology, t) {
  /**
   * Backend methodology text migration helper.
   *
   * The backend now sends stable methodology.note_keys so the frontend can own
   * the visible language through reports.json. Legacy methodology.notes remain
   * supported as a backward-compatible fallback while the report endpoints are
   * migrated incrementally.
   */
  const reportKind = methodology?.report_kind;

  if (reportKind && Array.isArray(methodology?.note_keys) && methodology.note_keys.length > 0) {
    return methodology.note_keys.map((noteKey) =>
      t(`reports:methodology.${reportKind}.notes.${noteKey}`, {
        defaultValue: noteKey,
      })
    );
  }

  return Array.isArray(methodology?.notes) ? methodology.notes : [];
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const ClientRetentionLiteReportPage = () => {
  /**
   * UI translation hook.
   *
   * This page intentionally uses the existing common + reports namespaces.
   * Backend payload fields remain technical and stable; only human-facing
   * labels, hints, filters, errors and table headers are translated here.
   */
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole]);

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    searchText: "",
    showOnlyRepeatClients: false,
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for role === "user"                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for employee filter                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[ClientRetentionLiteReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:clientRetentionLite.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Fetch report                                                             */
  /* ------------------------------------------------------------------------ */

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchClientRetentionLiteReport(from, to, userIds);
        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[ClientRetentionLiteReportPage] ❌ Error fetching report:", err);
        setError(t("reports:clientRetentionLite.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null
        ? [currentUserId]
        : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived data                                                             */
  /* ------------------------------------------------------------------------ */

  const summary = report?.summary || {};
  const methodologyNotes = resolveMethodologyNotes(report?.methodology, t);

  const clientRows = Array.isArray(report?.client_rows) ? report.client_rows : [];
  const visitCountDistribution = Array.isArray(report?.visit_count_distribution)
    ? report.visit_count_distribution
    : [];
  const topRepeatClients = Array.isArray(report?.top_repeat_clients)
    ? report.top_repeat_clients
    : [];

  const visibleClientRows = useMemo(() => {
    let out = [...clientRows];

    if (filters.showOnlyRepeatClients) {
      out = out.filter((row) => row.is_repeat_client);
    }

    const searchText = String(filters.searchText || "").trim().toLowerCase();

    if (!searchText) return out;

    return out.filter((row) => {
      const client = String(row.client || "").toLowerCase();
      const bucket = String(row.retention_bucket || "").toLowerCase();

      return client.includes(searchText) || bucket.includes(searchText);
    });
  }, [clientRows, filters.showOnlyRepeatClients, filters.searchText]);

  const hasReportData = Boolean(report);

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="client-retention-lite-page">
      <div className="client-retention-lite-header">
        <div className="header-left">
          <h1>{t("reports:clientRetentionLite.title")}</h1>
          <p className="header-hint">{t("reports:clientRetentionLite.headerHint")}</p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:clientRetentionLite.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>

          <div className="filter-item filter-item-toggle">
            <label className="filter-label">{t("reports:clientRetentionLite.focus")}</label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={filters.showOnlyRepeatClients}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    showOnlyRepeatClients: e.target.checked,
                  }))
                }
              />
              <span>{t("reports:clientRetentionLite.showOnlyRepeatClients")}</span>
            </label>
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : hasReportData ? (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:clientRetentionLite.distinctClients")}</span>
                <span className="summary-value">
                  {formatCount(summary.distinct_clients_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:clientRetentionLite.totalVisits")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_visits)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:clientRetentionLite.totalRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary.total_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:clientRetentionLite.repeatClients")}</span>
                <span className="summary-value">
                  {formatCount(summary.repeat_clients_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:clientRetentionLite.oneTimeClients")}</span>
                <span className="summary-value">
                  {formatCount(summary.one_time_clients_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:clientRetentionLite.repeatRevenueShare")}</span>
                <span className="summary-value">
                  {formatPercentFromRatio(summary.repeat_revenue_share)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:clientRetentionLite.avgVisitsPerClient")}</span>
                <span className="summary-value">
                  {formatNumber(summary.avg_visits_per_client)}
                </span>
              </div>
            </div>

            <div className="highlight-grid">
              <div className="highlight-card">
                <div className="highlight-title">{t("reports:clientRetentionLite.repeatClientShare")}</div>
                <div className="highlight-metric">
                  {formatPercentFromRatio(summary.repeat_clients_share)}
                </div>
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:clientRetentionLite.oneTimeClientShare")}</div>
                <div className="highlight-metric">
                  {formatPercentFromRatio(summary.one_time_clients_share)}
                </div>
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:clientRetentionLite.repeatVisitShare")}</div>
                <div className="highlight-metric">
                  {formatPercentFromRatio(summary.repeat_visit_share)}
                </div>
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:clientRetentionLite.topRepeatClient")}</div>
                {summary?.top_repeat_client ? (
                  <>
                    <div className="highlight-name">{summary.top_repeat_client.client}</div>
                    <div className="highlight-metric">
                      {t("reports:clientRetentionLite.visitsCount", { count: formatCount(summary.top_repeat_client.total_visits) })}
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:clientRetentionLite.noRepeatClientInPeriod")}</div>
                )}
              </div>
            </div>

            <div className="methodology-card">
              <div className="methodology-title">{t("reports:clientRetentionLite.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:clientRetentionLite.visitCountDistribution")}</h2>
                <p>{t("reports:clientRetentionLite.visitCountDistributionDescription")}</p>
              </div>

              <div className="distribution-grid">
                {visitCountDistribution.map((row) => (
                  <div className="distribution-card" key={row.bucket}>
                    <div className="distribution-card__label">
                      {retentionBucketLabel(row.bucket, t)}
                    </div>
                    <div className="distribution-card__value">
                      {formatCount(row.clients_count)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:clientRetentionLite.topRepeatClients")}</h2>
                <p>{t("reports:clientRetentionLite.topRepeatClientsDescription")}</p>
              </div>

              <div className="table-wrapper">
                <table className="data-table data-table--compact">
                  <thead>
                    <tr>
                      <th>{t("reports:common.client")}</th>
                      <th>{t("reports:clientRetentionLite.visits")}</th>
                      <th>{t("reports:clientRetentionLite.revenuePln")}</th>
                      <th>{t("reports:clientRetentionLite.avgRevenuePerVisit")}</th>
                      <th>{t("reports:clientRetentionLite.avgDaysBetweenVisits")}</th>
                      <th>{t("reports:clientRetentionLite.bucket")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topRepeatClients.length > 0 ? (
                      topRepeatClients.map((row) => (
                        <tr key={row.client}>
                          <td>{row.client}</td>
                          <td>{formatCount(row.total_visits)}</td>
                          <td>{formatMoney(row.total_revenue)}</td>
                          <td>{formatMoney(row.avg_revenue_per_visit)}</td>
                          <td>
                            {row.avg_days_between_visits != null
                              ? formatNumber(row.avg_days_between_visits)
                              : "—"}
                          </td>
                          <td>
                            <span className={retentionBadgeClassName(row.retention_bucket)}>
                              {retentionBucketLabel(row.retention_bucket, t)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="empty-table-cell">
                          {t("reports:clientRetentionLite.noRepeatClientsInCurrentSelection")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:clientRetentionLite.detailedClientRows")}</h2>
                <p>{t("reports:clientRetentionLite.detailedClientRowsDescription")}</p>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.client")}</th>
                      <th>{t("reports:clientRetentionLite.visits")}</th>
                      <th>{t("reports:clientRetentionLite.revenuePln")}</th>
                      <th>{t("reports:clientRetentionLite.bookedMinutes")}</th>
                      <th>{t("reports:clientRetentionLite.avgRevenuePerVisit")}</th>
                      <th>{t("reports:clientRetentionLite.avgMinutesPerVisit")}</th>
                      <th>{t("reports:clientRetentionLite.firstVisit")}</th>
                      <th>{t("reports:clientRetentionLite.lastVisit")}</th>
                      <th>{t("reports:clientRetentionLite.avgDaysBetweenVisits")}</th>
                      <th>{t("reports:clientRetentionLite.bucket")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleClientRows.length > 0 ? (
                      visibleClientRows.map((row) => (
                        <tr key={row.client}>
                          <td>{row.client}</td>
                          <td>{formatCount(row.total_visits)}</td>
                          <td>{formatMoney(row.total_revenue)}</td>
                          <td>{formatNumber(row.total_booked_minutes)}</td>
                          <td>{formatMoney(row.avg_revenue_per_visit)}</td>
                          <td>{formatNumber(row.avg_minutes_per_visit)}</td>
                          <td>{row.first_visit_at ? String(row.first_visit_at).slice(0, 10) : "—"}</td>
                          <td>{row.last_visit_at ? String(row.last_visit_at).slice(0, 10) : "—"}</td>
                          <td>
                            {row.avg_days_between_visits != null
                              ? formatNumber(row.avg_days_between_visits)
                              : "—"}
                          </td>
                          <td>
                            <span className={retentionBadgeClassName(row.retention_bucket)}>
                              {retentionBucketLabel(row.retention_bucket, t)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="empty-table-cell">
                          {t("reports:clientRetentionLite.noClientRowsMatchFilters")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default ClientRetentionLiteReportPage;

